// AUResidencyBanner — AU data-residency trust signal (mockups Fig27/Fig34/Fig41).
// Reassures users at high-trust decision points (sign-in, the two upload entry
// points) that their IP never leaves Australia and stays under their own keys.
// Purely informational — matches the app's light theme, no dark variant.

import { useState, type ReactNode } from 'react'
import { Lock, X } from 'lucide-react'

interface AUResidencyBannerProps {
  /** stable id — dismissal is remembered per id when `dismissible` is set. */
  id?: string
  /** when true, shows a dismiss control and remembers the choice in localStorage.
   *  Defaults to false: every mockup this banner is drawn from shows it as a
   *  permanent trust signal, not a one-off tip. */
  dismissible?: boolean
  className?: string
  children?: ReactNode
}

const STORAGE_PREFIX = 'au-residency-banner-dismissed:'

export default function AUResidencyBanner({
  id = 'default',
  dismissible = false,
  className = '',
  children,
}: AUResidencyBannerProps) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!dismissible) return false
    try {
      return localStorage.getItem(STORAGE_PREFIX + id) === '1'
    } catch {
      return false
    }
  })

  if (dismissed) return null

  function dismiss() {
    setDismissed(true)
    try {
      localStorage.setItem(STORAGE_PREFIX + id, '1')
    } catch {
      /* storage unavailable — dismissal holds for this render only */
    }
  }

  return (
    <div
      role="note"
      aria-label="Australian data residency"
      className={`flex items-start gap-2.5 rounded-lg border border-iflow-200 bg-iflow-50 px-3.5 py-3 text-sm text-iflow-900 ${className}`}
    >
      <Lock size={16} className="mt-0.5 shrink-0 text-amber-500" aria-hidden />
      <p className="flex-1 leading-relaxed">
        {children ?? (
          <>
            Your data is encrypted and hosted entirely in <strong>Australia (ap-southeast-2)</strong>.
            Only you hold the keys.
          </>
        )}
      </p>
      {dismissible && (
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 text-iflow-400 hover:text-iflow-600"
        >
          <X size={14} />
        </button>
      )}
    </div>
  )
}
